import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Per-user `is_favorite` annotation for tone lists fetched via PostgREST
 * (feeds querying `tones_counts` directly, where the RPC-side computation in
 * search_tones_a2 / get_tone_with_models isn't available).
 *
 * One batched query against the unique index on favorites (user_id, tone_id)
 * per page of results. Works with the admin client, and with JWT-scoped
 * clients too (RLS lets a user read their own favorites rows).
 */
export async function getFavoriteToneIds(
  supabase: SupabaseClient,
  userId: string | null | undefined,
  toneIds: ReadonlyArray<number>
): Promise<Set<number>> {
  if (!userId || toneIds.length === 0) return new Set();
  const { data, error } = await supabase
    .from('favorites')
    .select('tone_id')
    .eq('user_id', userId)
    .in('tone_id', toneIds as number[]);
  if (error) throw error;
  return new Set((data ?? []).map((row: { tone_id: number }) => row.tone_id));
}

/** Map `is_favorite` onto each tone from a set of favorited tone ids. */
export function annotateIsFavorite<T extends { id: number | null }>(
  tones: ReadonlyArray<T>,
  favoriteIds: ReadonlySet<number>
): Array<T & { is_favorite: boolean }> {
  return tones.map((tone) => ({
    ...tone,
    is_favorite: tone.id != null && favoriteIds.has(tone.id),
  }));
}

/**
 * Convenience wrapper: fetch the favorite set for the page of tones and
 * annotate in one call.
 */
export async function withIsFavorite<T extends { id: number | null }>(
  supabase: SupabaseClient,
  userId: string | null | undefined,
  tones: ReadonlyArray<T>
): Promise<Array<T & { is_favorite: boolean }>> {
  const ids = tones
    .map((t) => t.id)
    .filter((id): id is number => id != null);
  const favoriteIds = await getFavoriteToneIds(supabase, userId, ids);
  return annotateIsFavorite(tones, favoriteIds);
}
